import {Component, PanelLifecycleContext} from 'panel';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import * as colors from 'common/util/colors';
import {assignOrdinalNumbersToSegmentKeys} from 'common/report/util';
import {SavedLegend, SavedLegendItem} from 'common/report/constants';
import {ColumnGroup, Columns, TableColumn, TableRow} from 'common/components/data-table/types';
import {SortConfig} from 'common/report/util/sorting/types';
import {createRowCompareFunc} from 'common/report/util/sorting/table-sorting';
import {Subscribable} from 'common/types/context/subscribable';

export interface SegmentItem {
  id: string;
  label: string;
  // support raw color strings to match any color the chart segments use, the chart segments are assumed to use the correct colors
  color?: string;
  // a list of additional segments per section to select when toggled
  relatedSegmentsPerSection?: string[][];

  // associated table row for this item. used in mp-segment-filter-data-table
  tableRow?: TableRow;
  // Note: if any item has a comparisonTableRow, all items are expected to have a comparisonTableRow (same if any item doesn't have a comparisonTableRow),
  // this is a quirk/limitation with how the underlying Data Table widget handles comparison rows
  comparisonTableRow?: TableRow;
}

export type ReadOnlySegmentItem = Pick<SegmentItem, 'id' | 'label' | 'color'>;

export interface SegmentSection {
  label?: string;
  items: SegmentItem[];

  // items that are always present for display purposes, non-interactive
  readOnlyItems?: ReadOnlySegmentItem[];

  // useful if you expect to getSections() manually to implement custom logic
  metadata?: any;
  /*
  Does this segment section have more data than is shown on the client side?
  */
  apiResultsTruncated?: boolean;
  /*
  Goes along with `apiResultsTruncated`; when the limited segments don't represent the "top" of available data, this suppresses labeling as "12 of top 100"
  */
  suppressShowingTop?: boolean;

  // associated data table columns for this group of items
  tableColumns?: Columns;
  // optional value formatter for data cells in the data table
  tableValueFormatter?: any;
}

export type HoveredItem = {
  seriesId: string;
};

export type HoverCallbackArgs = {hovered: HoveredItem | null; prevHovered: HoveredItem | null};
type HoverCallback = ({hovered, prevHovered}: HoverCallbackArgs) => void;

/**
 * SelectMode controls how segments are selected in the data table.
 *
 * SelectMode.Position means that when you change sort order, it'll change segment selections to select top segments
 * based on the new sort order. You only enter SelectMode.Position by changing the segments returned by the query
 * (e.g. add/removing a breakdown). When loading a bookmark, you start in SelectMode.Items (i.e. the selected segments
 * from the bookmark are used). And if you click "Select Top Segments" or select individual segments, you'll enter
 * SelectMode.Items.
 */
export enum SelectMode {
  Item = `items`, // User selected segments
  Position = `positional`, // The top N segments
}

function getSegmentIdsPerSection(sections: SegmentSection[]): string[][] {
  return sections.map((section) => section.items.map((item) => item.id));
}

function mapToSet(arr: string[][]): Array<Set<string>> {
  return arr.map((subarr) => new Set(subarr));
}

// Note: this manual equality check is significantly faster than calling lodash/isEqual on sections
function sectionSetEquals(sections1: Array<Set<string>>, sections2: Array<Set<string>>): boolean {
  if (sections1?.length !== sections2?.length) {
    return false;
  }
  for (let sectionIdx = 0; sectionIdx < sections1.length; sectionIdx++) {
    const set1 = sections1[sectionIdx];
    const set2 = sections2[sectionIdx];
    if (set1.size !== set2.size) {
      return false;
    }
    for (const item of set1) {
      if (!set2.has(item)) {
        return false;
      }
    }
  }
  return true;
}

function sectionsContainSavedLegend(sections: Array<Set<string>>, savedLegend: Array<Set<string>>): boolean {
  if (sections?.length !== savedLegend?.length) {
    return false;
  }
  for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx++) {
    const section = sections[sectionIdx];
    const saved = savedLegend[sectionIdx];
    for (const item of saved) {
      if (!section.has(item)) {
        return false;
      }
    }
  }
  return true;
}

export function getItemsSortedByTableRow(section: SegmentSection, tableSortConfig: SortConfig): SegmentItem[] {
  if (!Array.isArray(section.tableColumns)) {
    throw new Error(`Segment section must have table columns`);
  }
  if (section.items.some((item) => !item.tableRow)) {
    throw new Error(`Segment items must have table rows`);
  }

  const compareFunc = createRowCompareFunc({
    sortConfig: tableSortConfig,
    columns: section.tableColumns.flatMap((column): TableColumn[] => {
      if ((column as ColumnGroup).columns) {
        return (column as ColumnGroup).columns;
      }
      return [column as TableColumn];
    }),
    rows: section.items.map((item) => item.tableRow),
  });

  return section.items.slice().sort((a, b) => {
    return compareFunc(a.tableRow, b.tableRow);
  });
}

/**
 * Generates legend from segment filter controller state
 * Use this method to convert current state to a saveable bookmark
 * @param selectedIdsPerSection
 * @param assignedColorsPerSection
 */
export function generateSavedLegend(
  selectedIdsPerSection: Array<Set<string>>,
  assignedColorsPerSection: Array<Map<string, number>>,
): SavedLegend[] {
  const legend: SavedLegend[] = [];

  for (let i = 0; i < selectedIdsPerSection.length; i++) {
    const selectedIds = Array.from(selectedIdsPerSection[i].values());
    const legendItems: SavedLegendItem[] = selectedIds.map((id) => {
      return {
        key: id,
        // TODO(reviewer) - is this logic correct? I want to make sure that we save the information correctly
        // Only save a color if it was assigned one. This shouldn't be an issue when this context was initialized
        // from a saved bookmark (since they should map to the same color persisted), but for other legends, I think
        // that some of them won't have assigned colors? just want to get another pair of eyes on this
        ...(assignedColorsPerSection[i].has(id) ? {color: assignedColorsPerSection[i].get(id)} : {}),
      };
    });

    legend.push({
      selected: legendItems,
    });
  }

  // We display legends from last breakdown -> first so it's more intuitive to the user + the more important sections are first,
  // but when saving we want them to be in the order matching the group_by
  return legend.reverse();
}

export function idsAndAssignedColorsFromLegend(
  savedLegend: SavedLegend[],
): {
  selectedIdsPerSection: string[][];
  assignedColorsPerSection: Array<Map<string, number>>;
} {
  const idsPerSection: string[][] = [];
  const colorOverrides: Array<Map<string, number>> = [];

  for (let i = 0; i < savedLegend.length; i++) {
    const legendSection = savedLegend[i];
    const colorsForThisSection = new Map<string, number>();
    const ids: string[] = [];

    for (const item of legendSection.selected) {
      ids.push(item.key);
      // Only the final section should have color, non-final ones have default color 1
      const color = i === savedLegend.length - 1 ? item.color : 1;
      if (color) {
        colorsForThisSection.set(item.key, color);
      }
    }

    idsPerSection.push(ids);
    colorOverrides.push(colorsForThisSection);
  }

  // N.B. We reverse the sections since bookmarks save legends in the groupBy order, but we want to show the most
  // distinguishing legend section first, which is the final breakdown
  return {
    selectedIdsPerSection: idsPerSection.reverse(),
    assignedColorsPerSection: colorOverrides.reverse(),
  };
}

export const DEFAULT_SELECT_ALL_LIMIT = 1000;

export enum TrackedToggleType {
  Select = `select`,
  DeSelect = `de-select`,
  SelectAll = `select all`,
  DeSelectAll = `de-select all`,
  SelectAllWithSearch = `select all matching search`,
  DeSelectAllWithSearch = `de-select all matching search`,
  Internal = `internal`,
}

type ToggleTrackingInfo = {
  toggleType: TrackedToggleType;
  isFormula?: boolean;
};

export type ToggleCallbackArgs = {trackingInfo: ToggleTrackingInfo};

type ToggleCallback = (args?: ToggleCallbackArgs) => void;

export class SegmentFilterController implements PanelLifecycleContext, Subscribable {
  private sections: SegmentSection[] | null = null;
  private selectedIdsPerSection: Array<Set<string>> = [];
  /** Segment hovered in the data table or legend */
  private legendHovered: HoveredItem | null = null;
  private legendHoverCallbacks: Set<HoverCallback> = new Set();
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];
  private toggleCallbacks: Set<ToggleCallback> = new Set();
  private canApplyDefaults = true;
  private defaultIdsPerSection: string[][] | null = null;
  private assignedColorsPerSection: Array<Map<string, number>> = [];
  private _tableSortConfig: null | SortConfig = null;
  private _segmentationQueryLimit: number;
  private _exportSegmentationQueryLimit: number;

  private _defaultNumSelections = null;
  private _maxNumSelections = null;
  private _selectMode = SelectMode.Item;

  public defaultSortConfig: SortConfig = null;

  get defaultNumSelections() {
    return this._defaultNumSelections;
  }

  get maxNumSelections() {
    return this._maxNumSelections;
  }

  public get tableSortConfig(): null | SortConfig {
    return this._tableSortConfig;
  }

  private updateConnectedComponents(): void {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
  private invokeToggleCallbacks(toggleCallbackArgs: ToggleCallbackArgs): void {
    for (const callback of this.toggleCallbacks) {
      callback(toggleCallbackArgs);
    }
  }

  /**
   * Clears and replaces all legend items, applies @persistedSelectionsForEachSection once if it changes from a previous value.
   * If supplied with the same value as previously, this method is a noop and does not affect the current selection set
   * @param orderedSections - sections of all available segments
   * @param options?.defaultNumSelections? - number of top segments to initially select
   * @param options?.maxNumSelections? - maximum number of segments that can be selected
   * @param options?.tableSortConfig? - sorts the segments according to the sort config if provided.
   * @param options?.defaultSortConfig? - default sorting for the data table, used for tracking and reset button in the sort menu
   *   tableSortConfig should be specified whenever the viz has an associated data table (which should be integrated
   *   using <mp-segment-filter-data-table>).
   */
  public loadSections(
    orderedSections: SegmentSection[],
    options: {
      defaultNumSelections: number;
      maxNumSelections: number;
      tableSortConfig?: SortConfig;
      defaultSortConfig?: SortConfig;
    },
  ): void {
    const oldSegmentIdsPerSection = this.sections ? getSegmentIdsPerSection(this.sections) : [];
    const segmentIdsPerSection = getSegmentIdsPerSection(orderedSections);

    // only reset selection if segments have actually changed and not just reordered
    const sectionsChanged = !sectionSetEquals(mapToSet(segmentIdsPerSection), mapToSet(oldSegmentIdsPerSection));
    this.sections = orderedSections;

    // needed for reports that use same SegmentFilter for all charts but charts have different limits
    this._defaultNumSelections = options.defaultNumSelections;
    this._maxNumSelections = options.maxNumSelections;
    this.defaultSortConfig = options.defaultSortConfig;

    const prevSelectMode = this._selectMode;
    if (options.tableSortConfig) {
      this._tableSortConfig = options.tableSortConfig;
      if (this.sections) {
        this.sections = this.sections.map((section) => {
          const sortedItems = getItemsSortedByTableRow(section, this.tableSortConfig);
          return {
            ...section,
            items: sortedItems,
          };
        });
      }
    } else {
      // Only data table legends are sortable. If `tableSortConfig` is not provided, then the legend isn't a data
      // table, SelectMode.Position doesn't make sense so we should reset to SelectMode.Item.
      this._selectMode = SelectMode.Item;
    }

    if (sectionsChanged) {
      this.assignedColorsPerSection = this.sections.map(() => new Map<string, number>());

      if (this.canApplyDefaults && this.defaultIdsPerSection) {
        this.loadPersistedSelections(this.defaultIdsPerSection);
        this.canApplyDefaults = false;
      } else {
        this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
      }
    } else if (
      // When switching from viz with data table -> bar viz, we'll set `this._selectMode = Select.Item` above. However,
      // previously it might've been `SelectMode.Position`. If that's the case, we want to reset to top selections for
      // bar viz. Hence, we use `prevSelectMode` for the check here.
      prevSelectMode === SelectMode.Position
    ) {
      this.assignedColorsPerSection = this.sections.map(() => new Map<string, number>());
      this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
    } else {
      this.updateConnectedComponents();
    }
  }

  public getTopSegments() {
    const segmentIdsPerSection = getSegmentIdsPerSection(this.sections);
    return [segmentIdsPerSection[0].slice(0, this.defaultNumSelections), ...segmentIdsPerSection.slice(1)];
  }

  // TODO: remove loadPersistedSelections() in favor of loadFromSavedLegend()
  public loadPersistedSelections(defaultIdsPerSection: string[][], {shouldInvokeToggleCallbacks = false} = {}) {
    if (this.canApplyDefaults && this.sections) {
      // if default selections have already been applied, persisted selections must override it everywhere so it must invoke toggle callbacks
      this.applySelections(defaultIdsPerSection, {
        shouldInvokeToggleCallbacks,
      });

      this.canApplyDefaults = false;
    } else {
      this.defaultIdsPerSection = defaultIdsPerSection;
    }
  }

  set selectMode(val: SelectMode) {
    this._selectMode = val;
  }

  get selectMode() {
    return this._selectMode;
  }

  public loadPersistedColors(assignedColors: Array<Map<string, number>>) {
    if (assignedColors && assignedColors.length) {
      this.assignedColorsPerSection = assignedColors;
      this.updateConnectedComponents();
    }
  }

  /**
   * Loads a saved legend from a bookmark, must be called *after* segments have been loaded with loadSections().
   * Note: Does *not* expect it to be reversed, the bookmark should be taken in raw from the bookmark,
   * and this method will deal with reversing it to display the last grouping first.
   * TODO: merge loadFromSavedLegend() into loadSections() because in practice they always need to be called together.
   * @param savedLegend - Legend from bookmark format
   */
  public loadFromSavedLegend(savedLegend: SavedLegend[] | null) {
    if (!savedLegend) {
      this.sections && this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
      return;
    }
    const {selectedIdsPerSection, assignedColorsPerSection} = idsAndAssignedColorsFromLegend(savedLegend);

    if (!this.sections) {
      throw new Error(`Must load available segments via loadSections() before loading saved legend.`);
    }
    const oldSegmentIdsPerSection = getSegmentIdsPerSection(this.sections);

    const savedLegendWorks = sectionsContainSavedLegend(
      mapToSet(oldSegmentIdsPerSection),
      mapToSet(selectedIdsPerSection),
    );
    if (savedLegendWorks) {
      this.applySelections(selectedIdsPerSection, {shouldInvokeToggleCallbacks: false});
      this.loadPersistedColors(assignedColorsPerSection);
    } else {
      this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
    }
  }

  public toBookmarkSavedLegend(): SavedLegend[] {
    return generateSavedLegend(this.selectedIdsPerSection, this.assignedColorsPerSection);
  }

  // "shouldInvokeToggleCallbacks" should only be false when applying default or existing selections.
  // Typically, we want to invoke toggle callbacks when a selection is caused by user input.
  // In practice, apps typically subscribe to toggle callbacks to update the bookmark or URL with active selections, so in these cases we skip toggle callbacks
  // to prevent an update loop where the URL-change causes a selection-change then the selection-change causes a URL-change again.
  public applySelections(defaultIdsPerSection: string[][], {shouldInvokeToggleCallbacks = true} = {}) {
    this.selectedIdsPerSection = mapToSet(defaultIdsPerSection);
    if (shouldInvokeToggleCallbacks) {
      this.invokeToggleCallbacks({trackingInfo: {toggleType: TrackedToggleType.Internal}});
    }
    this.updateConnectedComponents();
  }

  public toggleItem(sectionIdx: number, id: string): void {
    const selectedIds = this.selectedIdsPerSection[sectionIdx];
    const isDeSelect = selectedIds?.has(id);
    // best-effort check if this is a formula for tracking purposes. show-clause sections are stored last for bar chart.
    const isFormula = sectionIdx === this.selectedIdsPerSection.length - 1 && !/^[A-Z]+\./.test(id);

    if (isDeSelect) {
      selectedIds.delete(id);
      // remove saved color
      this.assignedColorsPerSection[sectionIdx].delete(id);
    } else {
      selectedIds.add(id);

      // select related segments
      const sectionItem = this.sections[sectionIdx].items.find((segment) => segment.id === id);
      if (sectionItem.relatedSegmentsPerSection) {
        this.selectedIdsPerSection = this.selectedIdsPerSection.map(
          (selectedIdsInSection, sectionIdx) =>
            new Set([...selectedIdsInSection, ...sectionItem.relatedSegmentsPerSection[sectionIdx]]),
        );
      }
    }

    this.invokeToggleCallbacks({
      trackingInfo: {
        toggleType: isDeSelect ? TrackedToggleType.DeSelect : TrackedToggleType.Select,
        isFormula,
      },
    });
    this.updateConnectedComponents();
  }

  /** Should be called when an item is hovered in the legend or data table (acting as a legend) */
  public legendHover(hovered: HoveredItem | null): void {
    const prevHovered = this.legendHovered;
    if (isEqual(hovered, prevHovered)) {
      return;
    }

    this.legendHovered = hovered;
    for (const callback of this.legendHoverCallbacks) {
      callback({hovered, prevHovered});
    }
  }

  public subscribeLegendHover(callback: HoverCallback): void {
    this.legendHoverCallbacks.add(callback);
  }
  public unsubscribeLegendHover(callback: HoverCallback): void {
    this.legendHoverCallbacks.delete(callback);
  }

  /**
   * @param ids? - If specified, will also return true if all ids in the Set are selected.
   */
  public isAllSelected(ids?: Set<string>): boolean {
    // select-all is only concerned with the first section
    return (
      this.selectedIdsPerSection[0].size === Math.min(this.sections[0].items.length, this.maxNumSelections) ||
      (Boolean(ids) && this._allIdsSelected(ids))
    );
  }

  public toggleOrderedTopItems(orderedIdsPerSection: string[][], {isSearchMode}: {isSearchMode?: boolean} = {}) {
    let toggledSelection: string[][];
    let trackedToggleType: TrackedToggleType;

    if (isSearchMode) {
      const displayedIds = new Set(orderedIdsPerSection[0]);
      if (this.isAllSelected(displayedIds)) {
        toggledSelection = [
          Array.from(this.selectedIdsPerSection[0]).filter((id) => !displayedIds.has(id)),
          ...orderedIdsPerSection.slice(1),
        ];

        trackedToggleType = TrackedToggleType.DeSelectAllWithSearch;
      } else {
        const selections = new Set(this.selectedIdsPerSection[0]);
        for (const id of orderedIdsPerSection[0]) {
          if (selections.size >= this.maxNumSelections) {
            break;
          }
          selections.add(id);
        }
        toggledSelection = [Array.from(selections), ...orderedIdsPerSection.slice(1)];

        trackedToggleType = TrackedToggleType.SelectAllWithSearch;
      }
    } else {
      toggledSelection = this.isAllSelected()
        ? [[], ...orderedIdsPerSection.slice(1)]
        : [orderedIdsPerSection[0].slice(0, this.maxNumSelections), ...orderedIdsPerSection.slice(1)];

      trackedToggleType = this.isAllSelected() ? TrackedToggleType.DeSelectAll : TrackedToggleType.SelectAll;
    }
    this.selectedIdsPerSection = mapToSet(toggledSelection);

    this.invokeToggleCallbacks({trackingInfo: {toggleType: trackedToggleType}});
    this.updateConnectedComponents();
  }

  public toggleAllItems(): void {
    const idsPerSection = getSegmentIdsPerSection(this.sections);
    this.toggleOrderedTopItems(idsPerSection);
  }

  public hasLoaded(): boolean {
    return this.sections !== null;
  }

  public getSections(): SegmentSection[] {
    return this.sections ?? [];
  }

  public getItems(sectionIdx: number): SegmentItem[] {
    return this.getSections()[sectionIdx]?.items ?? [];
  }

  public getDisplayedItems(sectionIdx: number): ReadOnlySegmentItem[] {
    const readOnlyItems = this.getSections()[sectionIdx]?.readOnlyItems ?? [];
    const selectedItems: ReadOnlySegmentItem[] = this.getSelectedItems(sectionIdx).map((item) =>
      pick(item, `id`, `label`, `color`),
    );
    return [...readOnlyItems, ...selectedItems];
  }

  public getSelectedItems(sectionIdx: number): SegmentItem[] {
    return this.getItems(sectionIdx).filter((item) => this.isItemSelected(sectionIdx, item.id));
  }

  public getSelectedCount(sectionIdx: number): number {
    return this.selectedIdsPerSection[sectionIdx]?.size ?? 0;
  }

  public isMaxSelected() {
    return this.getSelectedCount(0) >= this.maxNumSelections;
  }

  public isItemSelected(sectionIdx: number, id: string): boolean {
    return this.selectedIdsPerSection[sectionIdx]?.has(id) ?? false;
  }

  public set segmentationQueryLimit(limit: number) {
    this._segmentationQueryLimit = limit;
  }

  public get segmentationQueryLimit(): number {
    return this._segmentationQueryLimit;
  }

  public set exportSegmentationQueryLimit(limit: number) {
    this._exportSegmentationQueryLimit = limit;
  }

  public get exportSegmentationQueryLimit(): number {
    return this._exportSegmentationQueryLimit;
  }

  // Get the overridden color, otherwise default to the supplied ones.
  // This allows widgets to opt-in to the new color overrides
  // To opt in, you can just omit `color` in the legend segments when you pass them initially to the component
  public getItemColor(sectionIdx: number, id: string): string {
    // if it's selected, then we have to actually give it a color. If not, we dynamically determine color (unless given)
    if (this.isItemSelected(sectionIdx, id)) {
      const item = this.sections[sectionIdx].items.find((segmentItem) => segmentItem.id === id);

      // only assign colors for first section
      if (sectionIdx > 0 || item?.color) {
        return item?.color ?? null; // null means default checkbox color
      }

      if (this.assignedColorsPerSection[sectionIdx].has(id)) {
        return colors.getChartSegmentColorCombo(this.assignedColorsPerSection[sectionIdx].get(id) - 1).primary;
      }

      // otherwise: assign new colors, and save them
      const colorAssignments = assignOrdinalNumbersToSegmentKeys(
        this.assignedColorsPerSection[sectionIdx],
        Array.from(this.selectedIdsPerSection[sectionIdx].keys()).filter(
          // Make sure we don't re-assign colors that already have them
          (key) => !this.assignedColorsPerSection[sectionIdx].has(key),
        ),
        colors.charts.default.length,
      );
      this.assignedColorsPerSection[sectionIdx] = colorAssignments;

      return colors.getChartSegmentColorCombo(this.assignedColorsPerSection[sectionIdx].get(id) - 1).primary;
    } else {
      // Since we don't know what color this could take, set it to be a default gray color
      return colors.gray1;
    }
  }

  public subscribeToggle(callback: ToggleCallback): void {
    this.toggleCallbacks.add(callback);
  }
  public unsubscribeToggle(callback: ToggleCallback): void {
    this.toggleCallbacks.delete(callback);
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }
  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  public subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((subscription) => subscription !== callback);
    return this;
  }

  private _allIdsSelected(ids: Set<string>): boolean {
    const selectedIds = new Set(this.selectedIdsPerSection[0]);
    const idsIntersectSelectedIds = [...selectedIds].filter((id) => ids.has(id));
    return idsIntersectSelectedIds.length === ids.size;
  }
}
